import Vue from "vue";
import i18n from "./i18n";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const FORCE_RELOAD_DELAY_MS = 5000;
const FETCH_TIMEOUT_MS = 5000;
const VERSION_FILE = "version.json";
const AUTO_RELOAD_GUARD_KEY = "bo_auto_reload_done";
const CURRENT_BUILD = process.env.VUE_APP_BUILD_VERSION || "dev";
const IS_PRODUCTION = process.env.NODE_ENV === "production";

let started = false;
let notifiedVersion = null;

// Estado reactivo compartido: el Sidebar lo usa para mostrar el botón de
// actualización solo cuando hay una versión nueva pendiente de aplicar.
export const versionState = Vue.observable({ updateAvailable: false });

function translate(key) {
  const iso = localStorage.getItem("iso") || "es";
  const entry = i18n[key];

  if (!entry) return key;
  return entry[iso] || entry.es || key;
}

function openNotice(params) {
  const buefy = Vue.prototype.$buefy;

  if (!buefy || !buefy.snackbar) return;
  buefy.snackbar.open(params);
}

function autoReloadAlreadyUsed() {
  try {
    return sessionStorage.getItem(AUTO_RELOAD_GUARD_KEY) === "1";
  } catch (error) {
    return false;
  }
}

function markAutoReloadUsed() {
  try {
    sessionStorage.setItem(AUTO_RELOAD_GUARD_KEY, "1");
  } catch (error) {
    // Sin sessionStorage se permite un único intento igualmente.
  }
}

async function clearBrowserCaches() {
  if (typeof window === "undefined" || !window.caches) return;

  try {
    const keys = await window.caches.keys();
    await Promise.all(keys.map((key) => window.caches.delete(key)));
  } catch (error) {
    // La recarga debe continuar aunque falle la limpieza de cachés.
  }
}

export async function hardReload() {
  await clearBrowserCaches();
  window.location.reload();
}

async function fetchServerVersion() {
  if (typeof fetch === "undefined") return null;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${VERSION_FILE}?ts=${Date.now()}`, {
      cache: "no-cache",
      signal: controller.signal,
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data && data.v ? data : null;
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

function showNewVersionNotice(data) {
  if (notifiedVersion === data.v) return;

  notifiedVersion = data.v;
  openNotice({
    message: translate("newVersionAvailable"),
    type: "is-info",
    position: "is-bottom",
    indefinite: true,
    actionText: translate("reload"),
    onAction: hardReload,
  });
}

function scheduleForcedReload(data) {
  if (autoReloadAlreadyUsed()) {
    // Ya se intentó una recarga automática en esta sesión: se ofrece la acción manual.
    showNewVersionNotice(data);
    return;
  }

  if (notifiedVersion === data.v) return;

  notifiedVersion = data.v;
  markAutoReloadUsed();
  openNotice({
    message: translate("updatingVersion"),
    type: "is-warning",
    position: "is-bottom",
    duration: FORCE_RELOAD_DELAY_MS,
  });
  setTimeout(hardReload, FORCE_RELOAD_DELAY_MS);
}

async function checkVersion() {
  if (!IS_PRODUCTION) return;

  const data = await fetchServerVersion();

  if (!data || data.v === CURRENT_BUILD) return;

  versionState.updateAvailable = true;
  if (data.force) {
    scheduleForcedReload(data);
  } else {
    showNewVersionNotice(data);
  }
}

export function initVersionCheck() {
  if (started || !IS_PRODUCTION) return;

  started = true;
  checkVersion();
  setInterval(checkVersion, CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkVersion();
  });
}

function isChunkLoadError(error) {
  if (!error) return false;

  const text = `${error.name || ""} ${error.message || String(error)}`;
  return /ChunkLoadError|Loading chunk|Loading CSS chunk/i.test(text);
}

export function reloadOnChunkError(error) {
  if (!IS_PRODUCTION || !isChunkLoadError(error)) return;
  if (autoReloadAlreadyUsed()) return;

  markAutoReloadUsed();
  hardReload();
}
